import { Transition } from '@headlessui/react'
import { ChevronDownIcon, PlusIcon } from '@heroicons/react/24/outline'
import { useState } from 'react'
import { Link, Navigate, Outlet, useLocation } from 'react-router'
import { useShallow } from 'zustand/react/shallow'

import telemetryEmptyIllustration from '@ui/assets/illustrations/telemetry_empty.svg'
import { Instance } from '@ui/interfaces'
import { useAuth } from '@ui/providers/AuthProvider'
import { useInstanceStore } from '@ui/stores'
import clsx from 'clsx'

export const InstanceNotificationWrapper = () => {
  const auth = useAuth()
  const { currentOrganization } = auth
  const orgId = currentOrganization?.id
  const location = useLocation()

  const instances: Instance[] = useInstanceStore(useShallow((state) => state.instances))
  const selectedInstanceKey = useInstanceStore(useShallow((state) => state.selectedInstanceKey))
  const setSelectedInstanceKey = useInstanceStore(useShallow((state) => state.setSelectedInstanceKey))

  const selectedInstance = instances.find((instance: Instance) => instance.connect_key === selectedInstanceKey)

  const [openInstanceSelector, setOpenInstanceSelector] = useState(false)

  const isActive =
    selectedInstance != undefined &&
    selectedInstance.minutesSinceLastHeartbeat != undefined &&
    selectedInstance?.minutesSinceLastHeartbeat <= 30

  // Our admin routes don't need an instance
  if (location.pathname.match('/manage') || location.pathname.match('/admin')) {
    return <Outlet />
  }

  // I have an instance and am going to the '/' page, redirect to the events page
  if (selectedInstance && location.pathname === '/') {
    return <Navigate to={`/instances/${selectedInstance.connect_key}/explore/events`} replace />
  }

  // If I don't have an instance and I'm going to '/', so to the manage page
  if (!selectedInstance && location.pathname === '/') {
    return <Navigate to={`/manage/${orgId}/instances`} replace />
  }

  // if I have a selected instance and it's active -- happy path!
  if (selectedInstance && isActive) {
    return <Outlet />
  }

  if (!selectedInstance && instances.length === 0) {
    return (
      <div className="container pt-24">
        <div className="mx-auto w-96 rounded-lg bg-white p-6 text-center">
          <h3 className="mt-2 text-sm font-semibold text-gray-900">Add an instance</h3>
          <p className="mt-1 text-sm text-gray-500">Get started by connecting a Datable instance.</p>
          <div className="mt-6 flex flex-row justify-center">
            <div className="mr-4">
              <Link
                to={`/organizations/${orgId}/instances`}
                className="inline-flex items-center rounded-md bg-primary-400 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600">
                <PlusIcon className="-ml-0.5 mr-1.5 h-5 w-5" aria-hidden="true" />
                Add Data
              </Link>
            </div>
            <DocsButton />
          </div>
        </div>
      </div>
    )
  }

  if (!selectedInstance || (!isActive && instances.length > 0)) {
    return (
      <div className="container mx-auto flex h-full w-full flex-col items-center justify-center">
        <div className="flex h-1/2 flex-col items-center">
          <img src={telemetryEmptyIllustration} alt="Empty pipeline" className="h-1/2 w-1/2" />
          <h1 className="my-3 text-center text-3xl font-medium text-gray-900">Selected instance is offline</h1>
          <p className="mb-4 text-gray-600">
            Your instance hasn&apos;t sent any data in the last 30 minutes. Select a different instance.
          </p>

          <div className="row relative flex justify-center">
            {/* Custom Instance Selector */}
            <div className="group relative transition-all">
              <div
                className={clsx(
                  'cursor-pointer rounded-md border border-gray-300 bg-white px-2 py-1 group-hover:text-primary-600'
                )}
                onClick={() => {
                  setOpenInstanceSelector(!openInstanceSelector)
                }}>
                Select instance
                <ChevronDownIcon className="ml-2 inline-block h-5 w-5 text-gray-400 group-hover:text-primary-600" />
              </div>
              <Transition
                show={openInstanceSelector}
                enter="transition-opacity duration-300"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="transition-opacity duration-300"
                leaveFrom="opacity-100"
                leaveTo="opacity-0">
                <div className="absolute left-0 top-10 z-10 w-40 rounded-md bg-white shadow-lg">
                  <div className="py-1">
                    {instances.map((instance: Instance) => (
                      <div
                        key={instance.connect_key}
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900">
                        <a
                          onClick={() => {
                            setSelectedInstanceKey(instance.connect_key)
                            setOpenInstanceSelector(false)
                          }}>
                          {instance.name}
                        </a>
                      </div>
                    ))}
                  </div>
                </div>
              </Transition>
            </div>

            <DocsButton />
          </div>
        </div>
      </div>
    )
  } else {
    return (
      <div className="container mx-auto flex h-full w-full flex-col items-center justify-center">
        <div className="flex h-1/2 flex-col items-center">
          <img src={telemetryEmptyIllustration} alt="Empty pipeline" className="h-1/2 w-1/2" />
          <h1 className="my-3 text-center text-3xl font-medium text-gray-900">Selected instance is offline</h1>
          <p className="mb-4 text-gray-600">
            Your instance went dark over 30 minutes ago. Make sure you have an active instance.
          </p>
          <DocsButton />
        </div>
      </div>
    )
  }
}

const DocsButton = () => (
  <div
    onClick={() => window.open('https://docs.datable.io', '_blank')}
    className={clsx(
      'ml-4 flex items-center justify-center rounded-md bg-primary-500 px-2',
      'cursor-pointer text-sm font-bold text-white transition-all hover:bg-primary-600'
    )}>
    See our docs
  </div>
)
